import { authFetch } from "@/lib/api-client";
import { fetchAmberOptions } from "@/lib/queries/amber-options";
import { API_REQUEST_TIMEOUT_LONG_MS } from "@/lib/query-constants";
import { resolveSourceFilter } from "@nexus/contracts";
import type { BrandVoice, GenerationStatus, OpportunitySet, StrategicPriorities } from "../types/opportunities.types";

// ─── Types ──────────────────────────────────────────────────────────

/**
 * API list response shape. Structurally identical to `OpportunitySet` today;
 * declaring it as an alias keeps any future divergence a compile error instead
 * of a silent drift between the API boundary and the component tree.
 */
export type OpportunitySetWithCounts = OpportunitySet;

export interface OpportunitySetRow {
  id: string;
  name: string;
  tenantId: string;
  createdById: string | null;
  /** Populated by the API on create/retry so the optimistic card can render "By <name>" without waiting for the list refetch. */
  createdByName: string | null;
  industry: string | null;
  benchmarkIndustry: string | null;
  myBrand: string | null;
  mainCompetitor: string | null;
  industryCompetitors: string | null;
  products: string[] | null;
  adPlatforms: string[] | null;
  status: GenerationStatus;
  generationStartedAt: string | null;
  generationCompletedAt: string | null;
  generationError: string | null;
  minimalGeneration: boolean;
  strategicPriorities: StrategicPriorities | null;
  brandVoice: BrandVoice | null;
  scriptGuidelines: string | null;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Read Endpoints ─────────────────────────────────────────────────

/**
 * Lists opportunity sets for the session tenant. Pass `tenantId` (active tenant) so callers
 * tie the request to the same tenant-scoped React Query cache key.
 */
export async function fetchOpportunitySets(context?: {
  tenantId: string;
}): Promise<OpportunitySetWithCounts[]> {
  void context?.tenantId;
  const { data } = await authFetch<OpportunitySetWithCounts[]>(
    "/opportunity-sets/list",
    // Tenant-wide list with full generation state — covers a cold pod first
    // hit + real DB fan-out without a mid-request abort.
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

// ─── Industries ─────────────────────────────────────────────────────

/** Page domain that scopes shared data endpoints to distinct permissions. */
export type DataScope =
  | "dashboard"
  | "overview"
  | "drilldown"
  | "opportunities"
  | "persona-opportunities";

export interface IndustryOption {
  index: string;
  title: string;
  assetCount90d?: number | null;
  dataUpdated?: string | null;
  /** Ad platform ids with competitor data; null until the industry is first synced. */
  competitorAdPlatforms?: string[] | null;
}

/** Persona industries catalog: eligible (default) vs leftover assigned picker. */
export type IndustryCatalog = "eligible" | "assigned";

export async function fetchIndustries(
  scope: DataScope,
  catalog?: IndustryCatalog,
): Promise<IndustryOption[]> {
  const path =
    catalog === "assigned" && scope === "persona-opportunities"
      ? `/${scope}/industries?catalog=assigned`
      : `/${scope}/industries`;
  const { data } = await authFetch<IndustryOption[]>(path);
  return data;
}

// ─── Products ────────────────────────────────────────────────────────

export interface ProductOption {
  id: string;
  name: string;
  isFocused: boolean;
  assetCount90d?: number | null;
}

export interface AdPlatformOption {
  id: string;
  name: string;
}

export async function fetchProducts(
  scope: DataScope,
  accountIds: string[],
  platform?: string | null,
): Promise<ProductOption[]> {
  const qs = new URLSearchParams({ groupByName: "true" });
  if (platform) qs.set("platform", platform);
  if (accountIds.length > 0) qs.set("accounts", accountIds.join(","));
  const { data } = await authFetch<ProductOption[]>(`/${scope}/tenant-products?${qs}`);
  return data;
}

/** Distinct product-type names for Create Set. No SKU query params. */
export async function fetchTenantProductTypes(
  scope: DataScope,
): Promise<{ name: string }[]> {
  const { data } = await authFetch<{ name: string }[]>(`/${scope}/tenant-product-types`);
  return data;
}

export async function fetchAdPlatforms(): Promise<AdPlatformOption[]> {
  const { data } = await authFetch<AdPlatformOption[]>("/mock-data/ad-platforms");
  return data;
}

// ─── Mutations ──────────────────────────────────────────────────────

export interface CreateOpportunitySetParams {
  name?: string;
  industry: string;
  benchmarkIndustry?: string | null;
  myBrand?: string | null;
  mainCompetitor?: string | null;
  industryCompetitors?: string[];
  products?: string[];
  adPlatforms?: string;
  strategicPriorities?: { focusOn?: string[]; avoid?: string[] };
  brandVoice?: { alwaysSay?: string; neverSay?: string };
  scriptGuidelines?: string;
  minimalGeneration?: boolean;
  startDate: string;
  endDate: string;
}

export interface BrandOption {
  name: string;
  imageUrl: string | null;
  assetCount90d?: number | null;
}

/**
 * Both lookups below are backed by the workspace worker, so the gateway answers
 * `200 { data }` on a warm cache and `202 { correlationId }` on a cold miss. The scope
 * stays in the URL for both the request and the status poll — the four Explore prefixes
 * are separately permissioned and must not be collapsed into one shared path.
 */
export async function fetchCompetitorOptions(
  scope: DataScope,
  industry: string,
  signal?: AbortSignal,
  platforms?: string[],
): Promise<BrandOption[]> {
  const params = new URLSearchParams({ industry });
  const source = resolveSourceFilter(platforms);
  if (source !== "All") {
    params.set("platforms", source);
  }
  return fetchAmberOptions<BrandOption[]>(
    `/${scope}/competitor-options?${params.toString()}`,
    `/${scope}`,
    { signal },
  );
}

export async function fetchCompetitorPlatformOptions(
  scope: DataScope,
  industry: string,
  signal?: AbortSignal,
): Promise<{ platforms: string[] }> {
  return fetchAmberOptions<{ platforms: string[] }>(
    `/${scope}/competitor-platform-options?industry=${encodeURIComponent(industry)}`,
    `/${scope}`,
    { signal },
  );
}

export async function fetchIndustryProducts(
  scope: DataScope,
  industry: string,
  signal?: AbortSignal,
): Promise<ProductOption[]> {
  return fetchAmberOptions<ProductOption[]>(
    `/${scope}/industry-products?industry=${encodeURIComponent(industry)}`,
    `/${scope}`,
    { signal },
  );
}

export async function createOpportunitySet(params: CreateOpportunitySetParams): Promise<OpportunitySetRow> {
  const { data } = await authFetch<OpportunitySetRow>(
    "/opportunity-sets",
    { method: "POST", body: JSON.stringify(params) },
  );
  return data;
}

/** Minimal fields the retry endpoint returns — only what changed. */
export interface RetryOpportunitySetResponse {
  id: string;
  replacedSetId: string;
  status: GenerationStatus;
  industry: string | null;
  createdByName: string | null;
  generationStartedAt: string | null;
  generationError: string | null;
}

/**
 * Retries generation for a FAILED set. Backend clears prior opportunities,
 * flips status to GENERATING, re-publishes the Pub/Sub event, and returns
 * only the fields that changed so the caller can patch the list cache.
 */
export async function retryOpportunitySet(
  id: string,
  params: { industry?: string } = {},
): Promise<RetryOpportunitySetResponse> {
  const { data } = await authFetch<RetryOpportunitySetResponse>(
    `/opportunity-sets/${encodeURIComponent(id)}/retry`,
    { method: "POST", body: JSON.stringify(params) },
  );
  return data;
}

export async function renameOpportunitySet(
  id: string,
  name: string,
): Promise<OpportunitySetRow> {
  const { data } = await authFetch<OpportunitySetRow>(
    `/opportunity-sets/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ name }) },
  );
  return data;
}

export async function deleteOpportunitySet(id: string): Promise<void> {
  await authFetch(
    `/opportunity-sets/${encodeURIComponent(id)}`,
    { method: "DELETE", body: JSON.stringify({}), rawResponse: true },
  );
}
